import { Flex, Image } from "@govlaunch/web";
import Box, { BoxProps } from "@govlaunch/web/src/box/Box";
import React from "react";
import { FlexProps } from "../box/Flex";
import { GalleryStateConsumer, IMediaType } from "./GalleryContext";
import VimeoThumbnail from "./thumbnails/VimeoThumbnail";
import { getVimeoId, getYoutubeId, getYoutubeThumbnail } from "./utils";

interface IGallerySelectedMediaProps extends BoxProps {
  storyTitle?: string;
}

export function GallerySelectedMedia({ storyTitle, ...props }: IGallerySelectedMediaProps) {
  return (
    <GalleryStateConsumer>
      {({ medias, selectedMediaIndex }) => {
        const selectedMedia = medias[selectedMediaIndex];

        return (
          <Box {...props}>
            {selectedMedia.type === IMediaType.Image && (
              <Image
                src={selectedMedia.url}
                alt={selectedMedia.alt || getImageAltText(storyTitle, selectedMediaIndex, false)}
                width="100%"
                maxWidth="100%"
                objectFit="cover"
                height={["120px", "200px", "300px", "419px"]}
              />
            )}

            {selectedMedia.type === IMediaType.Youtube && (
              <Box height={["120px", "200px", "300px", "419px"]}>
                <iframe
                  title="youtube"
                  id="ytplayer"
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${getYoutubeId(selectedMedia.url)}?autoplay=0`}
                  frameBorder="0"
                />
              </Box>
            )}

            {selectedMedia.type === IMediaType.Vimeo && (
              <Box height={["120px", "200px", "300px", "419px"]}>
                <iframe
                  title="vimeo"
                  id="vimeoplayer"
                  width="100%"
                  height="100%"
                  src={`https://player.vimeo.com/video/${getVimeoId(selectedMedia.url)}?autoplay=0&loop=0&autopause=0`}
                  frameBorder="0"
                />
              </Box>
            )}
          </Box>
        );
      }}
    </GalleryStateConsumer>
  );
}

interface IGalleryThumbnailsProps {
  props?: FlexProps;
  storyTitle?: string;
}

export function GalleryThumbnails({ storyTitle, props }: IGalleryThumbnailsProps) {
  return (
    <GalleryStateConsumer>
      {({ medias, selectedMediaIndex, onChange }) => (
        <Flex justify="center" wrap="wrap" {...props}>
          {medias.map((media, index) => {
            return (
              <Box
                as="button"
                // @ts-ignore
                type="button"
                width="93px"
                height="93px"
                border="2px"
                borderColor={index === selectedMediaIndex ? "blue.300" : "transparent"}
                borderRadius="md"
                padding="2px"
                key={media.url}
                onClick={() => onChange(index)}
                mr={2}
                mt={2}
                _focus={{
                  boxShadow: "outline",
                  outline: "none",
                }}
              >
                {media.type === IMediaType.Image && (
                  <Image
                    borderRadius="md"
                    src={media.url}
                    alt={media.alt || getImageAltText(storyTitle, index, true)}
                    objectFit="cover"
                    height="100%"
                    width="100%"
                    maxWidth="100%"
                    maxHeight="100%"
                  />
                )}

                {media.type === IMediaType.Youtube && (
                  <Image
                    borderRadius="md"
                    src={getYoutubeThumbnail(media.url)}
                    alt={media.alt || `story gallery media ${selectedMediaIndex + 1}`}
                    objectFit="cover"
                    height="100%"
                    width="100%"
                  />
                )}

                {media.type === IMediaType.Vimeo && (
                  <VimeoThumbnail selectedMediaIndex={selectedMediaIndex} media={media} />
                )}
              </Box>
            );
          })}
        </Flex>
      )}
    </GalleryStateConsumer>
  );
}

function getImageAltText(storyTitle: string | undefined, selectedMediaIndex: number, isTumbnail: boolean) {
  if (typeof storyTitle !== "undefined" && storyTitle.length > 0) {
    return `"${storyTitle}" story media${isTumbnail ? " thumbnail " : " "}${selectedMediaIndex + 1}`;
  }

  return `Gallery media${isTumbnail ? " thumbnail " : " "}${selectedMediaIndex + 1}`;
}
